import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Avatar,
  Badge,
  Button,
  DatePicker,
  Drawer,
  Empty,
  Grid,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  HeartOutlined,
  SearchOutlined,
  StopOutlined,
  UserAddOutlined,
  UndoOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import useAdmin from "../../hooks/useAdmin";
import { retryUnlessClientError, serverErrorMessage } from "../../lib/serverError";
import { useUpdateDonation } from "../../hooks/useUpdateDonation";
import { AdminPageHeader } from "../../components/admin-shell/AdminShell";
import DonorFormModal from "./DonorFormModal";
import "./donor.css";

const PAGE_SIZE = 30;
const DONATION_GAP_DAYS = 90;
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];
const SORTS = [
  { label: "Newest", value: "" },
  { label: "Blood group", value: "group" },
  { label: "Donated", value: "donated" },
  { label: "Never donated", value: "none-donated" },
  { label: "Name", value: "name" },
];

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";
const formatDateTime = (value) =>
  new Date(value).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const LastDonation = ({ date }) => {
  if (!date) return <span className="donor-muted">Never</span>;
  const days = Math.floor((Date.now() - new Date(date).getTime()) / (1000 * 3600 * 24));
  return (
    <div className="donor-cell-stack">
      <span>{formatDate(date)}</span>
      {days >= DONATION_GAP_DAYS ? (
        <span className="donor-ready">Ready to donate</span>
      ) : (
        <span className="donor-wait">Ready in {DONATION_GAP_DAYS - days} days</span>
      )}
    </div>
  );
};

/* ---------- Notes panel ---------- */

const NotesDrawer = ({ member, onClose }) => {
  const queryClient = useQueryClient();
  const { getNotes, addNote, deleteNote } = useAdmin();
  const [text, setText] = useState("");
  const userId = member?._id;

  useEffect(() => setText(""), [userId]);

  const { data: notes = [], isLoading, error: notesError } = useQuery({
    queryKey: ["admin-notes", userId],
    queryFn: () => getNotes(userId),
    enabled: !!userId,
    retry: retryUnlessClientError,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-notes", userId] });
    queryClient.invalidateQueries({ queryKey: ["admin-donors"] });
  };

  const { mutate: save, isPending: saving } = useMutation({
    mutationFn: addNote,
    onSuccess: () => {
      setText("");
      refresh();
    },
    onError: (err) => toast.error(serverErrorMessage(err, "Could not save the note")),
  });

  const { mutate: remove } = useMutation({
    mutationFn: deleteNote,
    onSuccess: refresh,
    onError: (err) => toast.error(serverErrorMessage(err, "Could not delete the note")),
  });

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    save({ userId, text: trimmed });
  };

  return (
    <Drawer
      className="adm-overlay"
      open={!!member}
      onClose={onClose}
      width={Math.min(440, window.innerWidth)}
      title={
        <div className="notes-head">
          <Avatar src={member?.profilePhoto || undefined} icon={<UserOutlined />} />
          <div>
            <div className="notes-head__name">{member?.name || "Member"}</div>
            <div className="notes-head__sub">
              {[member?.blood, member?.phone].filter(Boolean).join(" · ")}
            </div>
          </div>
        </div>
      }
    >
      <p className="notes-private">Only admins can see these notes.</p>
      <Input.TextArea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) submit();
        }}
        placeholder="e.g. Called on 12 Oct, will donate next month"
        autoSize={{ minRows: 3, maxRows: 8 }}
        maxLength={2000}
        showCount
      />
      <Button type="primary" block className="notes-add" loading={saving} disabled={!text.trim()} onClick={submit}>
        Add note
      </Button>

      <div className="notes-list">
        {isLoading ? (
          <div className="adm-empty">Loading notes…</div>
        ) : notesError ? (
          <div className="adm-empty">{serverErrorMessage(notesError, "Could not load notes.")}</div>
        ) : notes.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No notes yet" />
        ) : (
          notes.map((note) => (
            <div key={note._id} className="note">
              <p className="note__text">{note.text}</p>
              <div className="note__meta">
                <span>
                  {note.adminName || "Admin"} · {formatDateTime(note.createdAt)}
                </span>
                <Popconfirm
                  title="Delete this note?"
                  okText="Delete"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => remove(note._id)}
                >
                  <Button type="text" size="small" icon={<DeleteOutlined />} aria-label="Delete note" />
                </Popconfirm>
              </div>
            </div>
          ))
        )}
      </div>
    </Drawer>
  );
};

/* ---------- Record donation ---------- */

const DonationModal = ({ member, onClose, onDone }) => {
  const [date, setDate] = useState(null);
  const { mutate, isPending } = useUpdateDonation();

  useEffect(() => {
    if (member) setDate(null);
  }, [member]);

  const submit = () => {
    if (!date) return toast.error("Pick the donation date");
    mutate(
      { id: member._id, pack: 1, _date: date.toDate() },
      {
        onSuccess: () => {
          onDone();
          onClose();
        },
      }
    );
  };

  return (
    <Modal
      className="adm-overlay"
      centered
      open={!!member}
      title={`Record donation · ${member?.name || ""}`}
      okText="Save donation"
      onOk={submit}
      onCancel={onClose}
      okButtonProps={{ loading: isPending }}
    >
      <p className="donor-muted">One bag per donation. Donors need {DONATION_GAP_DAYS} days between donations.</p>
      <label className="donor-label" htmlFor="donation-date">
        Donation date
      </label>
      <DatePicker
        id="donation-date"
        value={date}
        onChange={setDate}
        format="D MMM YYYY"
        placeholder="Pick the date (or Today)"
        disabledDate={(d) => d && d.valueOf() > Date.now()}
        style={{ width: "100%" }}
      />
    </Modal>
  );
};

/* ---------- Page ---------- */

const AllDonor = () => {
  const screens = Grid.useBreakpoint();
  // Pinned columns only help when there's room for the rest of the table.
  const pinColumns = !!screens.lg;
  const queryClient = useQueryClient();
  const { getDonors, setBlocked } = useAdmin();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = useState(searchParams.get("q") || "");
  const [notesFor, setNotesFor] = useState(null);
  // null: closed, {}: adding, a donor row: editing that donor.
  const [formFor, setFormFor] = useState(null);
  const [donationFor, setDonationFor] = useState(null);

  // Filters live in the URL so they survive reloads and can be linked to.
  const text = searchParams.get("q") || "";
  const blood = searchParams.get("blood") || "";
  const sortBy = searchParams.get("sort") || "";
  const status = searchParams.get("status") || "";

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchText.trim() !== text) setFilter("q", searchText.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]); // eslint-disable-line react-hooks/exhaustive-deps

  const donors = useInfiniteQuery({
    queryKey: ["admin-donors", text, blood, sortBy, status],
    queryFn: ({ pageParam }) => getDonors({ skip: pageParam, limit: PAGE_SIZE, text, blood, sortBy, status }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((sum, p) => sum + p.users.length, 0);
      return loaded < last.count ? loaded : undefined;
    },
    refetchOnWindowFocus: false,
    retry: retryUnlessClientError,
  });

  const rows = useMemo(() => (donors.data?.pages || []).flatMap((p) => p.users), [donors.data]);
  const firstPage = donors.data?.pages?.[0];

  // Keep the open notes panel showing the latest row data.
  const notesMember = notesFor ? rows.find((r) => r._id === notesFor._id) || notesFor : null;

  const { mutate: toggleBlock } = useMutation({
    mutationFn: setBlocked,
    onSuccess: (_res, vars) => {
      toast.success(vars.isBan ? "Member blocked" : "Member unblocked");
      queryClient.invalidateQueries({ queryKey: ["admin-donors"] });
    },
    onError: (err) => toast.error(serverErrorMessage(err, "Could not update the member")),
  });

  const columns = [
    {
      title: "Donor",
      key: "donor",
      fixed: pinColumns ? "left" : undefined,
      width: 240,
      render: (_, row) => (
        <div className="donor-who">
          <Avatar size={38} src={row.profilePhoto || undefined} icon={<UserOutlined />} />
          <div className="donor-who__text">
            <div className="donor-who__name">
              {row.name || "Unnamed"}
              {row.isBan ? <Tag color="red" className="donor-who__tag">Blocked</Tag> : null}
            </div>
            <a href={`tel:${row.phone}`} className="donor-who__phone">
              {row.phone}
            </a>
          </div>
        </div>
      ),
    },
    {
      title: "Blood",
      dataIndex: "blood",
      key: "blood",
      width: 80,
      render: (value) => (value ? <span className="donor-blood">{value}</span> : <span className="donor-muted">—</span>),
    },
    {
      title: "Location",
      key: "location",
      width: 180,
      render: (_, row) => (
        <div className="donor-cell-stack">
          <span>{row.district || <span className="donor-muted">Not set</span>}</span>
          {row.upuzila ? <span className="donor-muted">{row.upuzila}</span> : null}
        </div>
      ),
    },
    {
      title: "Last donation",
      dataIndex: "lastDonation",
      key: "lastDonation",
      width: 160,
      render: (value) => <LastDonation date={value} />,
    },
    {
      title: "Number",
      dataIndex: "is_contact_public",
      key: "contact",
      width: 100,
      render: (value) =>
        value === false ? <Tag>Private</Tag> : <Tag color="green">Public</Tag>,
    },
    {
      title: "Notes",
      key: "notes",
      width: 220,
      render: (_, row) => (
        <button type="button" className="donor-notes" onClick={() => setNotesFor(row)}>
          <Badge count={row.noteCount} size="small" color="#006a4e" showZero={false}>
            <FileTextOutlined className="donor-notes__icon" />
          </Badge>
          <span className="donor-notes__text">{row.latestNote || "Add a note"}</span>
        </button>
      ),
    },
    {
      title: "",
      key: "actions",
      fixed: pinColumns ? "right" : undefined,
      width: 140,
      render: (_, row) => (
        <div className="donor-actions">
          <Tooltip title="Edit details">
            <Button icon={<EditOutlined />} aria-label={`Edit ${row.name || "donor"}`} onClick={() => setFormFor(row)} />
          </Tooltip>
          <Tooltip title="Record donation">
            <Button icon={<HeartOutlined />} aria-label="Record donation" onClick={() => setDonationFor(row)} />
          </Tooltip>
          {row.isBan ? (
            <Popconfirm title={`Unblock ${row.name || "this member"}?`} okText="Unblock" onConfirm={() => toggleBlock({ id: row._id, isBan: false })}>
              <Tooltip title="Unblock">
                <Button icon={<UndoOutlined />} aria-label="Unblock" />
              </Tooltip>
            </Popconfirm>
          ) : (
            <Popconfirm
              title={
                <>
                  Block {row.name || "this member"}?
                  <div className="donor-muted">Blocked members are hidden from donor search.</div>
                </>
              }
              okText="Block"
              okButtonProps={{ danger: true }}
              onConfirm={() => toggleBlock({ id: row._id, isBan: true })}
            >
              <Tooltip title="Block">
                <Button danger icon={<StopOutlined />} aria-label="Block" />
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  const expandedRowRender = (row) => (
    <dl className="donor-details">
      <div>
        <dt>Other numbers</dt>
        <dd>{[row.mobile1, row.mobile2].filter(Boolean).join(", ") || "—"}</dd>
      </div>
      <div>
        <dt>Email</dt>
        <dd>{row.email || "—"}</dd>
      </div>
      <div>
        <dt>Facebook</dt>
        <dd>
          {row.facebook ? (
            <a href={row.facebook} target="_blank" rel="noreferrer">
              {row.facebook}
            </a>
          ) : (
            "—"
          )}
        </dd>
      </div>
      <div>
        <dt>Present address</dt>
        <dd>{row.presentAddress || "—"}</dd>
      </div>
      <div>
        <dt>Permanent address</dt>
        <dd>{row.parmanentAddress || "—"}</dd>
      </div>
      <div>
        <dt>Profession</dt>
        <dd>{row.profession || "—"}</dd>
      </div>
      <div>
        <dt>Joined</dt>
        <dd>{formatDate(row.joinDate) || "—"}</dd>
      </div>
    </dl>
  );

  return (
    <>
      <AdminPageHeader
        title="Donors"
        actions={
          <Button type="primary" icon={<UserAddOutlined />} onClick={() => setFormFor({})}>
            Add donor
          </Button>
        }
        subtitle={
          firstPage
            ? firstPage.total != null
              ? `${Number(firstPage.count || 0).toLocaleString()} shown of ${firstPage.total.toLocaleString()} members`
              : `${Number(firstPage.count || 0).toLocaleString()} members`
            : donors.error
            ? serverErrorMessage(donors.error, "Could not load donors.")
            : "Loading…"
        }
      />

      <div className="adm-card donor-toolbar">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search name, phone, place, email, or a blood group like B+"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="donor-toolbar__search"
        />
        <Select
          value={blood}
          onChange={(v) => setFilter("blood", v)}
          className="donor-toolbar__select"
          options={[{ label: "All groups", value: "" }, ...BLOOD_GROUPS.map((g) => ({ label: g, value: g }))]}
        />
        <Select
          value={status}
          onChange={(v) => setFilter("status", v)}
          className="donor-toolbar__select"
          options={[
            { label: "Everyone", value: "" },
            { label: "Active", value: "active" },
            { label: "Blocked", value: "banned" },
          ]}
        />
        <Radio.Group
          value={sortBy}
          onChange={(e) => setFilter("sort", e.target.value)}
          optionType="button"
          options={SORTS}
          className="donor-toolbar__sort"
        />
      </div>

      <div className="adm-card donor-table">
        <Table
          rowKey="_id"
          size="middle"
          columns={columns}
          dataSource={rows}
          loading={donors.isLoading}
          pagination={false}
          scroll={{ x: 1080 }}
          expandable={{ expandedRowRender }}
          locale={{
            emptyText: donors.error
              ? serverErrorMessage(donors.error, "Could not load donors.")
              : "No donors match these filters",
          }}
        />
        {donors.hasNextPage ? (
          <div className="donor-more">
            <Button size="large" loading={donors.isFetchingNextPage} onClick={() => donors.fetchNextPage()}>
              Load {PAGE_SIZE} more
            </Button>
          </div>
        ) : null}
      </div>

      <NotesDrawer member={notesMember} onClose={() => setNotesFor(null)} />
      <DonorFormModal
        open={!!formFor}
        donor={formFor?._id ? formFor : null}
        onClose={() => setFormFor(null)}
      />
      <DonationModal
        member={donationFor}
        onClose={() => setDonationFor(null)}
        onDone={() => queryClient.invalidateQueries({ queryKey: ["admin-donors"] })}
      />
    </>
  );
};

export default AllDonor;
